/**
 * Despesas por Categoria — tabela + pizza colorida (Recharts).
 *
 * Backend persiste JSON {itens:[{historico,valor}], total_declarado, fonte_pagina}.
 * Aqui parseamos, ordenamos por valor desc, agrupamos categorias <2% em "Outros"
 * pra pizza ficar legivel (em meses com 22+ itens).
 *
 * Layout: lado a lado em desktop (lg+), empilhado em mobile.
 */
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { GLASS_CHART_CARD } from "../lib/chartTheme";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FileText, Loader2, Search, Sparkles, X } from "lucide-react";
import { api } from "../lib/api";

interface Props {
  json: string | null;
  competenciaId?: number;
  onVerPagina?: (pagina: number) => void;
  conta?: "UNC" | "FRE" | "FS"; // conta contábil deste gráfico (padrão UNC)
}

const NOME_CONTA: Record<string, string> = {
  UNC: "Conta Principal (UNC)",
  FRE: "Fundo de Reserva (FRE)",
  FS: "Fundo Salão (FS)",
};

// ---- Detalhamento de uma categoria (drill-down) ----
interface DocRef {
  pagina: number;
  tipo: string;    // "nf" | "boleto" | "recibo" | "doc" | "email"
  rotulo: string;  // "NF" | "Boleto" | "Recibo" | "Doc" | "E-mail"
}
interface DetalheItem {
  descricao: string;
  fornecedor: string | null;
  documento: string | null;
  valor: number;
  percentual: number;
  fonte_pagina: number | null;
  pagina_doc: number | null;
  documentos?: DocRef[] | null;
}
interface DetalheResp {
  categoria: string;
  total_categoria: number;
  fonte: string; // "servicos" | "balancete" | "ia" | "indisponivel"
  reconciliado: boolean;
  fonte_pagina: number | null;
  aviso: string | null;
  itens: DetalheItem[];
}

interface ItemRaw { historico: string; valor: string }
interface ServicoRaw { descricao: string; valor: string }
interface PayloadRaw {
  itens: ItemRaw[];
  total_declarado: string | null;
  fonte_pagina: string;
  servicos_detalhe?: ServicoRaw[] | null;
}
interface Item { historico: string; valor: number; pct: number }
interface ServicoLinha { descricao: string; valor: number; outros?: boolean }

// Agrupa os menores serviços (cauda que soma ≤15% do total) em "Outros".
// Mantém os maiores detalhados. Só agrupa se a cauda tiver ≥2 itens.
function agruparServicos(raw: ServicoRaw[]): { linhas: ServicoLinha[]; total: number } {
  const itens: ServicoLinha[] = raw
    .map((d) => ({ descricao: d.descricao, valor: Number(d.valor) }))
    .filter((d) => !Number.isNaN(d.valor) && d.valor > 0);
  itens.sort((a, b) => b.valor - a.valor);
  const total = itens.reduce((s, i) => s + i.valor, 0);
  if (total <= 0 || itens.length <= 2) return { linhas: itens, total };

  const limite = total * 0.15;
  let acc = 0;
  let corte = itens.length;
  for (let k = itens.length - 1; k >= 0; k--) {
    if (acc + itens[k].valor <= limite) { acc += itens[k].valor; corte = k; }
    else break;
  }
  const cauda = itens.slice(corte);
  if (cauda.length >= 2) {
    const cabeca = itens.slice(0, corte);
    cabeca.push({ descricao: `Outros (${cauda.length} itens)`, valor: acc, outros: true });
    return { linhas: cabeca, total };
  }
  return { linhas: itens, total };
}

// Paleta de 18 cores ciclica — alta saturacao, contraste OK em fundo claro
const PALETA = [
  "#2563eb", "#dc2626", "#16a34a", "#ea580c", "#9333ea",
  "#0891b2", "#ca8a04", "#db2777", "#475569", "#0d9488",
  "#7c3aed", "#65a30d", "#0284c7", "#e11d48", "#a16207",
  "#4338ca", "#15803d", "#be185d",
];
const COR_OUTROS = "#94a3b8";  // slate-400

function fmtBRL(n: number): string {
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function parsePayload(json: string | null): PayloadRaw | null {
  if (!json) return null;
  try { return JSON.parse(json); } catch { return null; }
}

function preparar(payload: PayloadRaw): { itens: Item[]; total: number; declarado: number | null; fonte: string } {
  const itens: Item[] = payload.itens.map((it) => ({
    historico: it.historico,
    valor: Number(it.valor),
    pct: 0,
  })).filter((it) => !Number.isNaN(it.valor) && it.valor !== 0);
  itens.sort((a, b) => b.valor - a.valor);
  const total = itens.reduce((s, it) => s + it.valor, 0);
  itens.forEach((it) => { it.pct = total > 0 ? (it.valor / total) * 100 : 0; });
  return {
    itens,
    total,
    declarado: payload.total_declarado != null ? Number(payload.total_declarado) : null,
    fonte: payload.fonte_pagina,
  };
}

function preparaParaPizza(itens: Item[]): { nome: string; valor: number; cor: string; pct: number }[] {
  const LIMITE_PCT = 2; // agrupar < 2%
  const grandes = itens.filter((it) => it.pct >= LIMITE_PCT);
  const pequenas = itens.filter((it) => it.pct < LIMITE_PCT);
  const fatias = grandes.map((it, i) => ({
    nome: it.historico,
    valor: it.valor,
    cor: PALETA[i % PALETA.length],
    pct: it.pct,
  }));
  if (pequenas.length > 0) {
    const somaPq = pequenas.reduce((s, it) => s + it.valor, 0);
    const pctPq = pequenas.reduce((s, it) => s + it.pct, 0);
    fatias.push({
      nome: `Outros (${pequenas.length} categorias)`,
      valor: somaPq,
      cor: COR_OUTROS,
      pct: pctPq,
    });
  }
  return fatias;
}

interface TooltipPayload { name: string; value: number; payload: { pct: number } }
function TooltipPizza({ active, payload }: { active?: boolean; payload?: TooltipPayload[] }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0];
  return (
    <div className="rounded-md shadow-lg px-3 py-2 text-xs"
      style={{ background: "rgba(12,17,34,0.96)", border: "1px solid rgba(255,255,255,0.14)" }}>
      <div className="font-semibold text-slate-100">{p.name}</div>
      <div className="text-slate-300 tabular-nums mt-0.5">{fmtBRL(p.value)} · {p.payload.pct.toFixed(1)}%</div>
    </div>
  );
}

function DetalheCategoriaModal({
  competenciaId,
  categoria,
  conta,
  onFechar,
  onVerPagina,
}: {
  competenciaId: number;
  categoria: string;
  conta: string;
  onFechar: () => void;
  onVerPagina?: (p: number) => void;
}) {
  const [ia, setIa] = useState<DetalheResp | null>(null);
  const { data: livre, isLoading } = useQuery<DetalheResp>({
    queryKey: ["despesa-detalhe", competenciaId, conta, categoria],
    queryFn: () =>
      api.get<DetalheResp>(
        `/competencias/${competenciaId}/despesas/detalhe?categoria=${encodeURIComponent(categoria)}&conta=${conta}`,
      ),
  });
  const detalharIA = useMutation({
    mutationFn: () =>
      api.post<DetalheResp>(`/competencias/${competenciaId}/despesas/detalhe-ia`, { categoria, conta }),
    onSuccess: (d) => setIa(d),
  });
  const res = ia ?? livre;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{categoria}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Itens desta categoria na <b>{NOME_CONTA[conta] ?? conta}</b>
              {res ? ` · ${fmtBRL(res.total_categoria)}` : ""}
              {res?.fonte === "ia"
                ? " · via IA"
                : res?.fonte === "servicos"
                ? " · do balancete"
                : res?.fonte === "documento"
                ? " · notas fiscais no PDF"
                : ""}
            </p>
          </div>
          <button type="button" onClick={onFechar} className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 size={16} className="animate-spin" /> Carregando…
            </div>
          ) : res && res.itens.length > 0 ? (
            <>
              {!res.reconciliado && (
                <div className="mb-3 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  {res.aviso ||
                    "A soma dos itens não fecha exatamente o total da categoria — confira na página do balancete."}
                </div>
              )}
              <div className="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-700 text-slate-100 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Item</th>
                      <th className="px-3 py-2 text-right font-semibold">Valor</th>
                      <th className="px-3 py-2 text-right font-semibold w-16">%</th>
                      {onVerPagina && <th className="px-3 py-2 w-10"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {res.itens.map((it, k) => (
                      <tr key={k} className={k % 2 === 0 ? "bg-white" : "bg-slate-50/60"}>
                        <td className="px-3 py-2 text-left text-slate-900">
                          <div className="font-medium">{it.descricao}</div>
                          {(it.fornecedor || it.documento) && (
                            <div className="text-[11px] text-slate-500">
                              {it.fornecedor ?? ""}
                              {it.fornecedor && it.documento ? " · " : ""}
                              {it.documento ? `doc ${it.documento}` : ""}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-800">
                          {res.fonte === "documento" && it.valor <= 0 ? "—" : fmtBRL(it.valor)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                          {res.fonte === "documento" && it.valor <= 0
                            ? "—"
                            : `${it.percentual.toFixed(1)}%`}
                        </td>
                        {onVerPagina && (
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {it.fonte_pagina != null && (
                              <button
                                type="button"
                                title={`Ver no balancete (página ${it.fonte_pagina})`}
                                onClick={() => onVerPagina(it.fonte_pagina!)}
                                className="text-brand-700 hover:text-brand-800"
                              >
                                <Search size={14} />
                              </button>
                            )}
                            {(it.documentos && it.documentos.length > 0
                              ? it.documentos
                              : it.pagina_doc != null && it.pagina_doc !== it.fonte_pagina
                                ? [{ pagina: it.pagina_doc, tipo: "doc", rotulo: "Doc" }]
                                : []
                            )
                              .filter((d) => d.pagina !== it.fonte_pagina)
                              .map((d) => (
                                <button
                                  key={`${d.tipo}-${d.pagina}`}
                                  type="button"
                                  title={`Ver ${d.rotulo} (página ${d.pagina})`}
                                  onClick={() => onVerPagina(d.pagina)}
                                  className={`ml-1.5 inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[11px] ${
                                    d.tipo === "nf" || d.tipo === "boleto"
                                      ? "text-brand-700 border-brand-200 bg-brand-50 hover:bg-brand-100"
                                      : "text-slate-600 border-slate-200 bg-slate-50 hover:bg-slate-100"
                                  }`}
                                >
                                  <FileText size={11} /> {d.rotulo}
                                </button>
                              ))}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-sm text-slate-600">
              {res?.aviso || "Este balancete não traz o detalhamento desta categoria em texto."}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mt-4">
            {res && res.fonte !== "ia" && (
              <button
                type="button"
                onClick={() => detalharIA.mutate()}
                disabled={detalharIA.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 disabled:opacity-50"
                title="A IA lê as páginas e extrai os itens desta categoria. Tem um pequeno custo (entra no Uso & Custos)."
              >
                {detalharIA.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Sparkles size={14} />
                )}
                {res.itens.length === 0 ? "Detalhar com IA (tem custo)" : "Refinar com IA (tem custo)"}
              </button>
            )}
            {onVerPagina && res?.fonte_pagina != null && (
              <button
                type="button"
                onClick={() => onVerPagina(res.fonte_pagina!)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:border-slate-400"
              >
                <Search size={14} /> Abrir página do balancete
              </button>
            )}
          </div>
          {detalharIA.isError && (
            <p className="text-xs text-rose-600 mt-2">Não consegui detalhar com IA agora.</p>
          )}
        </div>

        <div className="flex justify-end p-4 border-t border-slate-200">
          <button type="button" onClick={onFechar} className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

export function DespesasCategoriaSection({ json, competenciaId, onVerPagina, conta = "UNC" }: Props) {
  const [catSel, setCatSel] = useState<string | null>(null);
  const nomeConta = NOME_CONTA[conta] ?? conta;
  const payload = parsePayload(json);
  if (!payload || payload.itens.length === 0) return null;

  const { itens, total, declarado, fonte } = preparar(payload);
  const fatias = preparaParaPizza(itens);
  const corPorHistorico = new Map<string, string>();
  itens.forEach((it, i) => {
    if (it.pct >= 2) corPorHistorico.set(it.historico, PALETA[i % PALETA.length]);
    else corPorHistorico.set(it.historico, COR_OUTROS);
  });
  const discrepancia = declarado != null && Math.abs(declarado - total) > 0.05;
  const servicos =
    payload.servicos_detalhe && payload.servicos_detalhe.length > 0
      ? agruparServicos(payload.servicos_detalhe)
      : null;

  return (
    <section className="mt-8">
      <div className="rounded-t-xl bg-gradient-to-r from-rose-700 to-orange-600 px-5 py-4 text-white shadow-sm">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold">Despesas por Categoria — {nomeConta}</h3>
            <p className="text-xs text-rose-100/90 mt-0.5">
              {conta === "UNC"
                ? "Somente a conta principal (UNC). FRE e FS têm balancete próprio."
                : "Categorias montadas pelo app a partir do razão desta conta (o balancete não traz um resumo por categoria dela)."}
            </p>
          </div>
          <span className="text-xs text-rose-100 bg-rose-800/40 border border-rose-500/30 px-2 py-1 rounded">Origem: {fonte}</span>
        </div>
      </div>
      <div className="border border-slate-200 border-t-0 rounded-b-xl bg-white px-5 py-5">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Tabela — ocupa 3/5 em desktop */}
          <div className="lg:col-span-3">
            <div className="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-800 text-slate-100 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2.5 text-left font-semibold w-6"></th>
                    <th className="px-3 py-2.5 text-left font-semibold">Histórico</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Débito</th>
                    <th className="px-3 py-2.5 text-right font-semibold w-16">%</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((it, k) => {
                    const clicavel = competenciaId != null;
                    return (
                      <tr
                        key={k}
                        className={`${k % 2 === 0 ? "bg-white" : "bg-slate-50/60"} hover:bg-rose-50/40 transition-colors ${clicavel ? "cursor-pointer" : ""}`}
                        onClick={clicavel ? () => setCatSel(it.historico) : undefined}
                        title={clicavel ? "Clique para ver os itens desta categoria" : undefined}
                      >
                        <td className="px-3 py-2.5">
                          <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: corPorHistorico.get(it.historico) || COR_OUTROS }} />
                        </td>
                        <td className="px-3 py-2.5 text-left font-medium text-slate-900">
                          {clicavel ? (
                            <span className="inline-flex items-center gap-1 hover:text-rose-700 hover:underline">
                              {it.historico}
                              <Search size={12} className="text-slate-400" />
                            </span>
                          ) : (
                            it.historico
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">{fmtBRL(it.valor)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{it.pct.toFixed(1)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-rose-50 border-t-2 border-rose-200">
                  <tr>
                    <td className="px-3 py-2.5"></td>
                    <td className="px-3 py-2.5 text-left font-semibold text-slate-800">Total</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-bold text-slate-900">{fmtBRL(total)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-bold text-slate-900">100,0%</td>
                  </tr>
                  {declarado != null && (
                    <tr>
                      <td className="px-3 py-2.5"></td>
                      <td className="px-3 py-2.5 text-left text-xs text-slate-600">Total declarado no PDF</td>
                      <td className={`px-3 py-2.5 text-right tabular-nums text-xs ${discrepancia ? "text-rose-700 font-semibold" : "text-slate-600"}`}>{fmtBRL(declarado)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-xs text-slate-500">{discrepancia ? "≠ soma" : "✓"}</td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
          </div>
          {/* Pizza — ocupa 2/5 em desktop */}
          <div className="lg:col-span-2 flex flex-col items-center justify-start">
            <div className="w-full border rounded-2xl p-2" style={{ ...GLASS_CHART_CARD, minHeight: 320 }}>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={fatias}
                    dataKey="valor"
                    nameKey="nome"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    innerRadius={50}
                    paddingAngle={1}
                    stroke="#0c1122"
                    strokeWidth={2}
                  >
                    {fatias.map((f, i) => (<Cell key={i} fill={f.cor} />))}
                  </Pie>
                  <Tooltip content={<TooltipPizza />} />
                  <Legend
                    verticalAlign="bottom"
                    iconType="square"
                    wrapperStyle={{ fontSize: 11, lineHeight: "16px" }}
                    formatter={(v: string) => <span className="text-slate-200">{v}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-slate-500 text-center mt-2">
              Categorias com menos de 2% agrupadas em &quot;Outros&quot;
            </p>
          </div>
        </div>

        {/* Detalhe de Serviços prestados (do corpo do balancete) */}
        {servicos && servicos.linhas.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold text-slate-800 mb-2">
              Detalhe — Serviços prestados
            </h4>
            <div className="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-700 text-slate-100 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2.5 text-left font-semibold">Serviço</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Valor</th>
                    <th className="px-3 py-2.5 text-right font-semibold w-16">%</th>
                  </tr>
                </thead>
                <tbody>
                  {servicos.linhas.map((s, k) => (
                    <tr key={k} className={`${k % 2 === 0 ? "bg-white" : "bg-slate-50/60"} ${s.outros ? "italic text-slate-500" : ""}`}>
                      <td className="px-3 py-2.5 text-left font-medium text-slate-900">{s.descricao}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">{fmtBRL(s.valor)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">
                        {servicos.total > 0 ? ((s.valor / servicos.total) * 100).toFixed(1) : "0,0"}%
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100 border-t-2 border-slate-200">
                  <tr>
                    <td className="px-3 py-2.5 text-left font-semibold text-slate-800">Total Serviços prestados</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-bold text-slate-900">{fmtBRL(servicos.total)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-bold text-slate-900">100,0%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Itens extraídos do corpo do balancete. Os menores (≈15% do total) são agrupados em &quot;Outros&quot;. O gráfico acima mantém &quot;Serviços prestados&quot; como um único valor.
            </p>
          </div>
        )}

        {competenciaId != null && (
          <p className="text-[11px] text-slate-500 mt-3">
            💡 Clique em uma categoria para ver <b>quais despesas a compõem</b> (item,
            valor e %) — da <b>{nomeConta}</b>. Sem custo quando o balancete traz o
            detalhe; se não, dá para <b>detalhar com IA</b> (pequeno custo).
          </p>
        )}
      </div>

      {catSel != null && competenciaId != null && (
        <DetalheCategoriaModal
          competenciaId={competenciaId}
          categoria={catSel}
          conta={conta}
          onFechar={() => setCatSel(null)}
          onVerPagina={onVerPagina}
        />
      )}
    </section>
  );
}

// ---- Despesas do Fundo de Reserva (FRE): busca própria + estado zerado ----
interface DespesasContaResp {
  conta: string;
  sem_despesas: boolean;
  total: number;
  itens_n: number;
  reconciliado: boolean;
  aviso: string | null;
  resumo_json: string | null;
}

export function DespesasFundoReservaSection({
  competenciaId,
  onVerPagina,
}: {
  competenciaId: number;
  onVerPagina?: (pagina: number) => void;
}) {
  const { data, isLoading } = useQuery<DespesasContaResp>({
    queryKey: ["despesas-conta", competenciaId, "FRE"],
    queryFn: () =>
      api.get<DespesasContaResp>(`/competencias/${competenciaId}/despesas-conta?conta=FRE`),
  });
  if (isLoading || !data) return null;

  // Caso comum: fundo sem despesas no mês.
  if (data.sem_despesas) {
    return (
      <section className="mt-8">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4">
          <h3 className="text-lg font-semibold text-emerald-900">
            Despesas do Fundo de Reserva (FRE)
          </h3>
          <p className="text-sm text-emerald-800 mt-1">
            Neste mês o <b>Fundo de Reserva não teve despesas</b>. É o normal — o FRE é
            usado só <b>eventualmente</b> (obras específicas ou urgências).
          </p>
        </div>
      </section>
    );
  }

  // Teve despesa, mas a classificação não fecha o total (ou não achou itens):
  // NÃO mostramos um gráfico que poderia enganar — só o total e o aviso.
  if (data.itens_n === 0 || !data.resumo_json || !data.reconciliado) {
    return (
      <section className="mt-8">
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
          <h3 className="text-lg font-semibold text-amber-900">
            Despesas do Fundo de Reserva (FRE) — {fmtBRL(data.total)}
          </h3>
          <p className="text-sm text-amber-800 mt-1">
            {data.aviso ||
              "O Fundo de Reserva teve despesa neste mês, mas não consegui montar as categorias automaticamente. Abra o balancete do FRE para conferir."}
          </p>
        </div>
      </section>
    );
  }

  // Teve despesa e a classificação fechou o total: reaproveita o mesmo
  // gráfico/tabela da UNC (conta=FRE), com drill-down por categoria.
  return (
    <DespesasCategoriaSection
      json={data.resumo_json}
      competenciaId={competenciaId}
      onVerPagina={onVerPagina}
      conta="FRE"
    />
  );
}
